import fabric from '@/lib/fabric';
import type { DesignElement } from '@canvas/shared-types';
import { createFrameObject } from '@/lib/frames';
import { createDiagramShape } from '@/lib/diagramShapes';
import { loadFabricImage } from '@/lib/loadFabricImage';

function starPoints(spikes: number, outer: number, inner: number) {
  const points: { x: number; y: number }[] = [];
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;

  for (let i = 0; i < spikes; i++) {
    points.push({ x: Math.cos(rot) * outer, y: Math.sin(rot) * outer });
    rot += step;
    points.push({ x: Math.cos(rot) * inner, y: Math.sin(rot) * inner });
    rot += step;
  }
  return points;
}

function regularPolygonPoints(sides: number, radius: number) {
  const n = Math.max(3, Math.min(12, Math.round(sides)));
  const points: { x: number; y: number }[] = [];
  const offset = -Math.PI / 2;
  for (let i = 0; i < n; i += 1) {
    const a = offset + (i * 2 * Math.PI) / n;
    points.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
  }
  return points;
}

function attachMeta(obj: fabric.Object, element: DesignElement) {
  const meta = obj as fabric.Object & {
    elementId?: string;
    __uid?: string;
    name?: string;
  };
  meta.elementId = element.elementId;
  meta.name = element.name;
  meta.__uid = `el-${element.elementId}-${Date.now()}`;
}

function placeOnCanvas(canvas: fabric.Canvas, obj: fabric.Object): fabric.Object {
  canvas.selection = true;
  canvas.skipTargetFind = false;
  canvas.add(obj);
  canvas.setActiveObject(obj);
  obj.setCoords();
  canvas.requestRenderAll();
  return obj;
}

async function loadImageUrl(
  url: string,
  w: number,
  h: number,
  cx: number,
  cy: number
): Promise<fabric.Image> {
  const img = await loadFabricImage(url);
  const iw = img.width || w;
  const ih = img.height || h;
  const scale = Math.min(w / iw, h / ih);
  img.set({
    left: cx - (iw * scale) / 2,
    top: cy - (ih * scale) / 2,
    scaleX: scale,
    scaleY: scale,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
  });
  return img;
}

/**
 * Create a Fabric object from a catalog DesignElement and place it on the canvas.
 */
export async function insertDesignElement(
  canvas: fabric.Canvas,
  element: DesignElement,
  at?: { x: number; y: number }
): Promise<fabric.Object | null> {
  const cx = at?.x ?? canvas.getWidth() / 2;
  const cy = at?.y ?? canvas.getHeight() / 2;
  const { factory, defaultWidth: w, defaultHeight: h } = element;

  if (factory.type === 'image-url') {
    const img = await loadImageUrl(factory.url, w, h, cx, cy);
    attachMeta(img, element);
    return placeOnCanvas(canvas, img);
  }

  let obj: fabric.Object | null = null;

  if (element.kind === 'frame') {
    const shape =
      factory.type === 'shape' && factory.shape === 'circle'
        ? 'circle'
        : factory.type === 'shape' && factory.shape === 'roundedRect'
          ? 'roundedRect'
          : 'rect';
    obj = createFrameObject(shape, {
      left: cx - w / 2,
      top: cy - h / 2,
      width: w,
      height: h,
    });
  } else if (factory.type === 'shape') {
    const fill = factory.fill || '#0d7377';
    const stroke = factory.stroke;
    const strokeWidth = factory.strokeWidth ?? 0;

    switch (factory.shape) {
      case 'rect':
        obj = new fabric.Rect({
          width: w,
          height: h,
          fill,
          stroke,
          strokeWidth,
        });
        break;
      case 'roundedRect':
        obj = new fabric.Rect({
          width: w,
          height: h,
          rx: 16,
          ry: 16,
          fill,
          stroke,
          strokeWidth,
        });
        break;
      case 'circle':
        obj = new fabric.Ellipse({
          rx: w / 2,
          ry: h / 2,
          fill,
          stroke,
          strokeWidth,
        });
        break;
      case 'triangle':
        obj = new fabric.Triangle({
          width: w,
          height: h,
          fill,
          stroke,
          strokeWidth,
        });
        break;
      case 'line':
        obj = new fabric.Line([0, 0, w, 0], {
          stroke: stroke || fill || '#152033',
          strokeWidth: strokeWidth || 4,
        });
        break;
      case 'star':
        obj = new fabric.Polygon(starPoints(5, w / 2, w / 4), {
          fill,
          stroke,
          strokeWidth,
        });
        break;
      case 'polygon':
        obj = new fabric.Polygon(
          regularPolygonPoints(factory.sides || 6, Math.min(w, h) / 2),
          {
            fill,
            stroke,
            strokeWidth,
          }
        );
        break;
    }
  } else if (factory.type === 'text') {
    const isBody = element.elementId.includes('body') || factory.fontSize <= 20;
    if (isBody) {
      obj = new fabric.Textbox(factory.text, {
        fontSize: factory.fontSize,
        fontWeight: factory.fontWeight as string | number | undefined,
        fontFamily: factory.fontFamily || 'Source Sans 3, Arial, sans-serif',
        fill: factory.fill || '#152033',
        width: w,
      });
    } else {
      obj = new fabric.IText(factory.text, {
        fontSize: factory.fontSize,
        fontWeight: factory.fontWeight as string | number | undefined,
        fontFamily: factory.fontFamily || 'Source Sans 3, Arial, sans-serif',
        fill: factory.fill || '#152033',
      });
    }
  } else if (factory.type === 'fabric-json') {
    obj = new fabric.Rect({
      width: w,
      height: h,
      fill: '#0d7377',
      ...factory.object,
    });
  } else if (factory.type === 'diagram') {
    obj = createDiagramShape(factory.diagram, w, h, {
      fill: factory.fill ?? '#0d7377',
      stroke: factory.stroke ?? '#152033',
      strokeWidth: factory.strokeWidth ?? 2,
      label: factory.label,
    });
  }

  if (!obj) return null;

  obj.set({
    left: cx - w / 2,
    top: cy - h / 2,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
  });

  attachMeta(obj, element);
  return placeOnCanvas(canvas, obj);
}

/**
 * Replace canvas contents with a template's Fabric JSON.
 */
export function applyTemplateContent(
  canvas: fabric.Canvas,
  content: {
    width: number;
    height: number;
    objects: Record<string, unknown>[];
    background?: string;
    version?: string;
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      canvas.discardActiveObject();
      canvas.clear();
      canvas.setWidth(content.width);
      canvas.setHeight(content.height);
      canvas.setBackgroundColor(content.background || '#ffffff', () => undefined);

      canvas.loadFromJSON(
        {
          version: content.version || '5.3.0',
          objects: content.objects,
          background: content.background || '#ffffff',
        },
        () => {
          canvas.forEachObject((obj) => {
            obj.set({
              selectable: true,
              evented: true,
              hasControls: true,
              hasBorders: true,
              lockMovementX: false,
              lockMovementY: false,
            });
            obj.setCoords();
          });
          canvas.calcOffset();
          canvas.selection = true;
          canvas.skipTargetFind = false;
          canvas.requestRenderAll();
          resolve();
        }
      );
    } catch (err) {
      reject(err);
    }
  });
}
